import axios from "axios";
import { api } from "../components/link";

const selectorKey = "ikimina.session";
const profileKey = "ikimina.profile";
const authUrl = (path) => `${api()}api/auth/${path}`;
let csrfRequest;
const refreshes = new Map();

async function rejectDuplicatedSession(selector) {
  sessionStorage.setItem("ikimina.duplicateTab", "true");
  try {
    const csrf = await getCsrf();
    await axios.post(authUrl("logout"), {}, {
      withCredentials: true,
      _skipSecurity: true,
      headers: { "X-Auth-Session": selector, "X-XSRF-TOKEN": csrf },
    });
  } catch (_) {
  } finally {
    clearSession();
  }
}

function getCookie(name) {
  const prefix = `${name}=`;
  const value = document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith(prefix));
  return value ? decodeURIComponent(value.substring(prefix.length)) : "";
}

function newSelector() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function getSessionSelector() {
  return sessionStorage.getItem(selectorKey) || "";
}

export function getProfile() {
  try {
    return JSON.parse(sessionStorage.getItem(profileKey) || "null");
  } catch (_) {
    return null;
  }
}

export function setProfile(profile) {
  if (profile && profile.sessionId) {
    sessionStorage.setItem(selectorKey, profile.sessionId);
    sessionStorage.setItem(profileKey, JSON.stringify(profile));
    if (typeof profile.e === "string" && profile.e.trim()) {
      localStorage.setItem("engine", profile.e);
    } else {
      localStorage.removeItem("engine");
    }
    sessionStorage.removeItem("ikimina.duplicateTab");
  }
}

export function clearSession() {
  sessionStorage.removeItem(selectorKey);
  sessionStorage.removeItem(profileKey);
}

export async function refreshProfile() {
  const selector = getSessionSelector();
  if (!selector) return null;
  const response = await axios.get(authUrl("me"), { headers: { "X-Auth-Session": selector } });
  setProfile(response.data);
  return response.data;
}

export function authMode() {
  return process.env.REACT_APP_AUTH_MODE || "jwt";
}

export function getCsrf() {
  const token = getCookie("XSRF-TOKEN");
  if (token) return Promise.resolve(token);
  if (!csrfRequest) {
    csrfRequest = axios.get(authUrl("csrf"), { withCredentials: true, _skipSecurity: true })
      .then(() => getCookie("XSRF-TOKEN"))
      .finally(() => { csrfRequest = null; });
  }
  return csrfRequest;
}

async function prepare(config) {
  config.withCredentials = true;
  config.headers = config.headers || {};
  delete config.headers.Authorization;
  delete config.headers.authorization;
  const url = config.url || "";
  if (authMode() === "legacy" || config._skipSecurity || !url.startsWith(api())) return config;
  const selector = getSessionSelector();
  if (selector) config.headers["X-Auth-Session"] = selector;
  const method = (config.method || "get").toLowerCase();
  if (!["get", "head", "options"].includes(method) || url.includes("/ikimina/api")) {
    config.headers["X-XSRF-TOKEN"] = await getCsrf();
  }
  return config;
}

axios.interceptors.request.use(prepare);

axios.interceptors.response.use((response) => response, async (error) => {
  const config = error.config || {};
  const retryData = config.data;
  if (error.config) {
    error.config.data = undefined;
    if (error.config.headers) {
      delete error.config.headers.Authorization;
      delete error.config.headers.authorization;
      delete error.config.headers["X-Auth-Session"];
      delete error.config.headers["X-XSRF-TOKEN"];
    }
    if (error.response && error.response.config) {
      error.response.config.data = undefined;
      if (error.response.config.headers) {
        delete error.response.config.headers.Authorization;
        delete error.response.config.headers.authorization;
        delete error.response.config.headers["X-Auth-Session"];
        delete error.response.config.headers["X-XSRF-TOKEN"];
      }
    }
  }
  const selector = getSessionSelector();
  const url = config.url || "";
  if (error.response && error.response.status === 403
      && error.response.data && error.response.data.code === "PASSWORD_CHANGE_REQUIRED") {
    const profile = getProfile() || {};
    sessionStorage.setItem(profileKey, JSON.stringify({ ...profile, passwordChangeRequired: true }));
    window.location.hash = "#/ChangePassword";
    return Promise.reject(error);
  }
  const authRequest = url.includes("/api/auth/");
  if (authMode() !== "legacy" && selector && error.response && error.response.status === 401
      && !config._retried && !config._skipSecurity && !authRequest) {
    if (!refreshes.has(selector)) {
      const request = (async () => {
        const csrf = await getCsrf();
        return axios.post(authUrl("refresh"), {}, {
          withCredentials: true,
          _skipSecurity: true,
          headers: { "X-Auth-Session": selector, "X-XSRF-TOKEN": csrf },
        });
      })().finally(() => refreshes.delete(selector));
      refreshes.set(selector, request);
    }
    try {
      await refreshes.get(selector);
      config._retried = true;
      config.data = retryData;
      return axios(config);
    } catch (_) {
      clearSession();
      window.location.hash = "#/Login";
    }
  }
  return Promise.reject(error);
});

if (typeof window !== "undefined" && typeof window.fetch === "function" && !window.__ikiminaFetchInstalled) {
  const originalFetch = window.fetch.bind(window);
  window.__ikiminaFetchInstalled = true;
  window.fetch = async (input, init = {}) => {
    const requestUrl = typeof input === "string" ? input : input.url;
    const requestInput = input instanceof Request ? input.clone() : input;
    const url = new URL(requestUrl, window.location.href);
    // api() may be relative ("/backend/") while url.href is always absolute, so compare against the resolved base.
    const apiBase = new URL(api(), window.location.href).href;
    if (authMode() === "legacy" || !url.href.startsWith(apiBase)) {
      return originalFetch(input, init);
    }
    const selector = getSessionSelector();
    const method = (init.method || (requestInput instanceof Request ? requestInput.method : "GET")).toUpperCase();
    const headers = new Headers(requestInput instanceof Request ? requestInput.headers : undefined);
    new Headers(init.headers || {}).forEach((value, key) => headers.set(key, value));
    headers.delete("Authorization");
    if (selector) headers.set("X-Auth-Session", selector);
    if (!["GET", "HEAD", "OPTIONS"].includes(method) || url.pathname.endsWith("/ikimina/api")) {
      headers.set("X-XSRF-TOKEN", await getCsrf());
    }
    const options = { ...init, method, headers, credentials: "include" };
    const response = await originalFetch(requestInput instanceof Request ? requestInput.clone() : requestInput, options);
    if (response.status === 401 && selector && !url.pathname.includes("/api/auth/")) {
      try {
        const csrf = await getCsrf();
        await axios.post(authUrl("refresh"), {}, {
          withCredentials: true,
          _skipSecurity: true,
          headers: { "X-Auth-Session": selector, "X-XSRF-TOKEN": csrf },
        });
        return originalFetch(requestInput instanceof Request ? requestInput.clone() : requestInput, options);
      } catch (_) {
        clearSession();
        window.location.hash = "#/Login";
      }
    }
    return response;
  };
}

let tabChannel;
if (typeof window !== "undefined" && "BroadcastChannel" in window) {
  const tabNonce = newSelector();
  tabChannel = new BroadcastChannel("ikimina-auth-tabs");
  tabChannel.onmessage = (event) => {
    const message = event.data || {};
    const selector = getSessionSelector();
    if (message.type === "probe" && selector && message.selector === selector && message.nonce !== tabNonce) {
      tabChannel.postMessage({ type: "active", selector, probe: message.nonce, nonce: tabNonce });
    } else if (message.type === "active" && selector && message.selector === selector && message.probe === tabNonce) {
      rejectDuplicatedSession(selector);
    }
  };
  const selector = getSessionSelector();
  if (selector) {
    tabChannel.postMessage({ type: "probe", selector, nonce: tabNonce });
  }
}

export function notifySessionClosed() {
  if (tabChannel) {
    tabChannel.postMessage({ type: "closed", selector: getSessionSelector() });
  }
}
